import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  PurchaseContractForOrder,
  PurchaseContractLine,
  PurchaseContractLineDetails,
  PurchaseContractLineForCreate,
  PurchaseContractLineHeader,
  PurchaseContractLineList,
  PurchaseContractLineQuickCreate,
  PurchaseContractLineSelect,
  PurchaseOrderFromPreviousIds,
  PurchaseOrderLineQuickCreate,
} from '../../../models';
import { TmsWebBaseEditCreateService } from '../../base';

@Injectable({
  providedIn: 'root',
})
export class PurchaseContractLineService extends TmsWebBaseEditCreateService<
  PurchaseContractLine,
  PurchaseContractLineDetails
> {
  public refreshDetails = new Subject<void>();

  constructor() {
    super('PurchaseContractLine', $localize`Purchase Contract Line`);
  }

  getForPurchaseContractHeader(id: string): Observable<PurchaseContractLineHeader[]> {
    return this.httpService.get<PurchaseContractLineHeader[]>(`${this.apiUrl}/PurchaseContractHeader?id=${id}`);
  }

  getForPurchaseContractLineList(search: string): Observable<PurchaseContractLineList[]> {
    return this.httpService.get<PurchaseContractLineList[]>(
      `${this.apiUrl}/GetForPurchaseContractLineList?search=${search}`,
    );
  }

  quickCreate(PurchaseContractLineQuickCreate: PurchaseContractLineQuickCreate): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/QuickCreate`, PurchaseContractLineQuickCreate);
  }

  getSelectItems(supplierId: string, productId: string): Observable<PurchaseContractLineSelect[]> {
    return this.httpService.get<PurchaseContractLineSelect[]>(
      `${this.apiUrl}/GetSelectItems?supplierId=${supplierId}&productId=${productId}`,
    );
  }

  // purchaseOrderLineId is the line on screen (left out of the before balance); quantity and unitOfMeasureId
  // are what is on screen, for the balance after it. Each is optional.
  getForPurchaseOrder(
    id: string,
    purchaseOrderLineId?: string,
    quantity?: number,
    unitOfMeasureId?: string,
  ): Observable<PurchaseContractForOrder> {
    const params = new URLSearchParams({ id });
    if (purchaseOrderLineId) {
      params.set('purchaseOrderLineId', purchaseOrderLineId);
    }
    if (quantity != null && !isNaN(+quantity)) {
      params.set('quantity', String(+quantity));
    }
    if (unitOfMeasureId) {
      params.set('unitOfMeasureId', unitOfMeasureId);
    }
    return this.httpService.get<PurchaseContractForOrder>(`${this.apiUrl}/GetForPurchaseOrder?${params}`);
  }

  getOutstandingContracts(productId: string): Observable<PurchaseContractLineForCreate[]> {
    return this.httpService.get<PurchaseContractLineForCreate[]>(
      `${this.apiUrl}/GetOutstandingContracts?productId=${productId}`,
    );
  }

  getFromContract(supplierId: string, search: string): Observable<PurchaseContractLine[]> {
    return this.httpService.get<PurchaseContractLine[]>(
      `${this.apiUrl}/GetFromContract?supplierId=${supplierId}&search=${search}`,
    );
  }

  createFromContract(
    purchaseOrderHeaderId: string,
    ids: PurchaseOrderFromPreviousIds[],
  ): Observable<PurchaseOrderLineQuickCreate> {
    return this.httpService.post<PurchaseOrderLineQuickCreate>(`${this.apiUrl}/CreateFromContract`, {
      purchaseOrderHeaderId,
      ids,
    });
  }
}
